import type { FormEvent } from 'react';
import { Controller } from 'react-hook-form';
import { Button, Input, Label } from '@shared/components';
import type { TopUpFormState } from '../hooks/useTopUpForm';

interface Props {
    form: TopUpFormState;
    id: string;
    autoFocusAmount?: boolean;
}

const BLOCKED_REASON_ID = 'top-up-blocked-reason';
const REFERENCE_HINT = 'Helps match this payment later.';

export function TopUpForm({ form, id, autoFocusAmount = false }: Props) {
    const { control, submit, isSubmitting, formError, blockedReason } = form;
    const blocked = isSubmitting || blockedReason !== null;
    const guardedSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (blocked) {
            event.preventDefault();
            return;
        }
        void submit(event);
    };

    return (
        <form id={id} onSubmit={guardedSubmit} className="space-y-4" noValidate>
            <div>
                <Label htmlFor="top-up-amount" required>
                    Amount paid
                </Label>
                <Controller
                    name="amountGross"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id="top-up-amount"
                            type="text"
                            inputMode="decimal"
                            autoFocus={autoFocusAmount}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!error}
                            hint={error?.message}
                            hintId="top-up-amount-error"
                            aria-invalid={!!error}
                            aria-describedby={error ? 'top-up-amount-error' : undefined}
                        />
                    )}
                />
            </div>

            <div>
                <Label htmlFor="top-up-reference" required>
                    Payment reference
                </Label>
                <Controller
                    name="paymentReference"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id="top-up-reference"
                            type="text"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="e.g. card receipt no. or bank ref"
                            error={!!error}
                            hint={error?.message ?? REFERENCE_HINT}
                            hintId={error ? 'top-up-reference-error' : 'top-up-reference-hint'}
                            aria-invalid={!!error}
                            aria-describedby={error ? 'top-up-reference-error' : 'top-up-reference-hint'}
                        />
                    )}
                />
            </div>

            {formError && (
                <p role="alert" className="text-sm text-mp-error">
                    {formError}
                </p>
            )}

            <div>
                <Button
                    type="submit"
                    size="sm"
                    className="w-full"
                    ariaDisabled={blocked}
                    ariaDescribedBy={blockedReason ? BLOCKED_REASON_ID : undefined}
                >
                    Record payment
                </Button>
                {blockedReason && (
                    <p id={BLOCKED_REASON_ID} className="mt-1.5 text-xs text-mp-muted">
                        {blockedReason}
                    </p>
                )}
            </div>
        </form>
    );
}
